import { useCallback, useEffect, useRef, useState } from "react";

export function useDebouncedSearch({
  searchTerm,
  minLength = 3,
  delay = 400,
  fetchOptions,
  resetOnShortTerm = true,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const lastFetchedTermRef = useRef("");
  const initialFetchDoneRef = useRef(false);

  const runFetch = useCallback(
    async (term, { force = false } = {}) => {
      if (!force && term === lastFetchedTermRef.current) return;
      setLoading(true);
      try {
        const results = await fetchOptions(term);
        setOptions(results);
        lastFetchedTermRef.current = term;
        if (term === "") initialFetchDoneRef.current = true;
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchOptions]
  );

  const refetch = useCallback(
    (term) => runFetch(term ?? lastFetchedTermRef.current, { force: true }),
    [runFetch]
  );

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.length >= minLength) {
        setDebouncedTerm(searchTerm);
      } else {
        setDebouncedTerm("");
      }
    }, delay);

    return () => clearTimeout(handler);
  }, [searchTerm, minLength, delay]);

  // Fetch on debounced term
  useEffect(() => {
    const termToFetch = debouncedTerm;

    const shouldSkip =
      termToFetch === "" && (initialFetchDoneRef.current || !resetOnShortTerm);

    if (shouldSkip || termToFetch === lastFetchedTermRef.current) return;

    setLoading(true);
    fetchOptions(termToFetch)
      .then((results) => {
        lastFetchedTermRef.current = termToFetch;
        setOptions(results);
      })
      .catch(() => setOptions([]))
      .finally(() => {
        setLoading(false);
        if (termToFetch === "") {
          initialFetchDoneRef.current = true;
        }
      });
  }, [debouncedTerm, fetchOptions, resetOnShortTerm]);

  // Reset fetch when input is cleared (after debounce)
  useEffect(() => {
    if (searchTerm === "" && debouncedTerm !== "") {
      setLoading(true);
      fetchOptions("")
        .then((results) => {
          setOptions(results);
          lastFetchedTermRef.current = "";
        })
        .catch(() => setOptions([]))
        .finally(() => {
          setLoading(false);
          initialFetchDoneRef.current = true;
        });
    }
  }, [searchTerm, debouncedTerm, fetchOptions]);

  // Initial fetch on mount (only once)
  useEffect(() => {
    if (searchTerm === "" && !initialFetchDoneRef.current) {
      setLoading(true);
      fetchOptions("")
        .then((results) => {
          setOptions(results);
          lastFetchedTermRef.current = "";
        })
        .catch(() => setOptions([]))
        .finally(() => {
          setLoading(false);
          initialFetchDoneRef.current = true;
        });
    }
  }, [searchTerm, fetchOptions]);

  return {
    options,
    loading,
    debouncedTerm,
    refetch,
  };
}
